import { tokenService } from "@/infrastructure/auth/TokenService";
import { localProfileBalanceUrl } from "@/infrastructure/http/local-profile-balance-url";
import { handleUnauthorizedResponse } from "@/infrastructure/auth/authUtils";
import { ACCOUNT_BLOCKED_CODE } from "@/lib/account-blocked";
import { auth } from "@/lib/firebase";
import camelcaseKeys from "camelcase-keys";
import { signOut } from "firebase/auth";

export interface CurrencyRate {
  usdToToken: number;
  kztToUsd: number;
  rubToUsd: number;
}

export interface BalancesResponse {
  availableTokens: number;
  currencyRate?: CurrencyRate;
}

async function getAuthHeader(): Promise<Record<string, string>> {
  return tokenService.getAuthHeaders();
}

function getProfileApiUrl(): string {
  const base = process.env.NEXT_PUBLIC_PROFILE_API_URL;
  if (!base) throw new Error("NEXT_PUBLIC_PROFILE_API_URL is not set");
  return base.replace(/\/$/, "");
}

export class BalanceService {
  async getBalances(): Promise<BalancesResponse> {
    const baseUrl = getProfileApiUrl();
    const url = localProfileBalanceUrl(
      `${baseUrl}/api/v1/profile/balances`,
      typeof window === "undefined" ? undefined : window.location.href
    );

    const headers = await getAuthHeader();

    const response = await fetch(url, {
      method: "GET",
      headers: {
        ...headers,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      if (response.status === 403) {
        try {
          const errorPayload = (await response.clone().json()) as {
            code?: string;
            detail?: { code?: string };
          };
          if (
            errorPayload.code === ACCOUNT_BLOCKED_CODE ||
            errorPayload.detail?.code === ACCOUNT_BLOCKED_CODE
          ) {
            if (auth) {
              void signOut(auth);
            }
            handleUnauthorizedResponse("blocked");
          }
        } catch {
          // ignore malformed blocked payloads
        }
      }
      const text = await response.text();
      throw new Error(`Failed to fetch balances: ${response.status} ${text}`);
    }

    const data = await response.json();

    // console.log("BalanceService: Received balance data:", data);

    // Convert PascalCase to camelCase using camelcase-keys (same as original project)
    const processedData = camelcaseKeys(data, { deep: true });

    // console.log("BalanceService: Processed data:", processedData);

    return {
      availableTokens: processedData.availableTokens ?? 0,
      currencyRate: processedData.currencyRate,
    };
  }
}

export const balanceService = new BalanceService();
